import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Save } from "lucide-react";
import { FieldRow, buildPayload } from "./Field";

const Card = ({ record, fields, fixed, collection, onChanged }) => {
  const [form, setForm] = useState(record);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/crud/${collection}`, { ...buildPayload(fields, form), ...fixed });
      toast.success("Saved");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const del = async () => {
    if (!form.id) return onChanged();
    try { await api.delete(`/admin/crud/${collection}/${form.id}`); toast.success("Deleted"); onChanged(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="glass rounded-xl p-4" data-testid={`sub-${collection}-card`}>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {fields.map((f) => <FieldRow key={f.key} f={f} value={form[f.key]} onChange={(v) => set(f.key, v)} />)}
      </div>
      <div className="flex gap-2 mt-4">
        <Button size="sm" onClick={save} disabled={saving} className="btn-primary rounded-lg" data-testid={`sub-${collection}-save`}><Save size={13} className="mr-1.5" />{saving ? "Saving…" : "Save"}</Button>
        <Button size="sm" variant="outline" onClick={del} className="text-rose-400" data-testid={`sub-${collection}-del`}><Trash2 size={13} /></Button>
      </div>
    </div>
  );
};

export const SubRecordList = ({ collection, fields, fixed, singular }) => {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/data/${collection}`);
      setItems(data.filter((d) => Object.entries(fixed).every(([k, v]) => d[k] === v)));
    } catch { setItems([]); }
    setDrafts([]);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [collection, JSON.stringify(fixed)]);

  return (
    <div>
      {loading && <div className="text-slate-500 font-mono text-sm animate-pulse mb-3">Loading…</div>}
      <div className="space-y-3">
        {items.map((it) => <Card key={it.id} record={it} fields={fields} fixed={fixed} collection={collection} onChanged={load} />)}
        {drafts.map((d) => <Card key={d._k} record={{ ...fixed }} fields={fields} fixed={fixed} collection={collection} onChanged={load} />)}
      </div>
      {!loading && items.length === 0 && drafts.length === 0 && <div className="glass rounded-xl p-6 text-center text-slate-500 text-sm mb-3">No {singular.toLowerCase()}s yet.</div>}
      <Button variant="outline" onClick={() => setDrafts((d) => [...d, { _k: Date.now() }])} className="mt-3" data-testid={`sub-${collection}-add`}><Plus size={14} className="mr-2" />Add {singular}</Button>
    </div>
  );
};
